<script setup>
import { ref, watch } from "vue";
import { Field, ErrorMessage } from "vee-validate";
import IconSearch from "@/components/icons/IconSearch.vue";

const props = defineProps({
  inputId: {
    type: String,
    default: "exampleInput",
  },
  placeholder: {
    type: String,
    default: "Enter here",
  },
  modelValue: {
    type: String,
    default: "",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: "",
  },
  type: {
    type: String,
    default: "text",
  },
  hasBoldLabel: {
    type: Boolean,
    default: true,
  },
  name: {
    type: String,
    default: "",
  },
  background: {
    type: String,
    default: "transparent",
  },
  autocomplete: {
    type: Boolean,
    default: false,
  },
  autocompleteOptions: {
    type: Array,
    default: [],
  },
});

const textInputRoot = ref();
const inpType = computed(() => props.type);
const position = ref({ x: 0, y: 0, width: 0 });

const emit = defineEmits(["update:modelValue"]);

const inputValue = ref(props.modelValue);

watch(inputValue, (newValue) => {
  if (newValue === props.modelValue) return;
  emit("update:modelValue", newValue);
});

watch(
  () => props.modelValue,
  (val) => {
    inputValue.value = val;
  },
  { deep: true, immediate: true }
);

const showOrHidePassword = () => {
  if (!props.disabled) {
    const types = { text: "password", password: "text" };
    inpType.value = types[inpType.value];
  }
};

const isFocused = ref(false);

const calculatePosition = () => {
  if (textInputRoot.value) {
    const rect = textInputRoot.value.getBoundingClientRect();
    position.value = {
      x: rect.left + window.scrollX, // Account for scrolling
      y: rect.top + rect.height - window.scrollY, // Position below the input
      width: rect.width, // Match the input width
    };
  }
};

const onFocus = () => {
  isFocused.value = true;
  calculatePosition();
};

const onUnFocus = () => {
  isFocused.value = false;
};

const isAutoCompleteListVisible = computed(() => {
  return (
    props.autocomplete &&
    isFocused.value &&
    props.autocompleteOptions.length &&
    inputValue.value
  );
});

const filteredAutoCompleteOptions = computed(() => {
  return props.autocompleteOptions.filter((option) => {
    return (
      option.toLowerCase().includes(inputValue.value.toLowerCase()) &&
      option.toLowerCase() !== inputValue.value.toLowerCase()
    );
  });
});

const onAutoCompleteItemClick = (item) => {
  inputValue.value = item;
};

onMounted(() => {
  calculatePosition();
  window.addEventListener("resize", calculatePosition);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", calculatePosition);
});
</script>

<template>
  <div
    ref="textInputRoot"
    :class="['text-input w-100', { focused: isFocused }]"
    :style="{ background: background }"
    @blur="onUnFocus"
  >
    <label
      v-if="label"
      :for="inputId"
      class="label w-100 p-0 text-left text-decoration-none text-neutral-350"
      :class="{ 'fw-bold': hasBoldLabel }"
    >
      {{ label }}
    </label>
    <Field :name="name" v-model="inputValue" v-slot="{ meta, field }">
      <div
        :class="[
          'd-flex align-items-center inp-wrapper',
          { disabled: disabled },
          { 'is-invalid': !meta.valid && meta.touched },
        ]"
      >
        <input
          :name="name"
          :type="inpType"
          :id="inputId"
          :placeholder="placeholder"
          :disabled="disabled"
          :readonly="readonly"
          v-model="inputValue"
          v-bind="field"
          class="form-control w-100 px-2 custom-input text-neutral-600"
          @focus="onFocus"
          @blur="onUnFocus"
        />

        <IconSearch v-if="type === 'search'" style="cursor: pointer" />
      </div>
    </Field>
    <ErrorMessage :name="name" as="div" class="invalid-feedback" />

    <!-- Teleport the autocomplete list to body -->
    <Teleport to="body">
      <ul
        v-if="isAutoCompleteListVisible"
        class="autocomplete-list p-0 m-0 position-absolute"
        :style="{
          left: `${position.x}px`,
          top: `${position.y}px`,
          width: `${position.width}px`,
        }"
      >
        <li
          v-for="(option, index) in filteredAutoCompleteOptions"
          :key="index"
          class="autocomplete-item"
          @mousedown.prevent="onAutoCompleteItemClick(option)"
        >
          {{ option }}
        </li>
      </ul>
    </Teleport>
  </div>
</template>

<style scoped lang="scss">
.custom-input {
  border: 1px solid $neutral-150;
  height: 2.375rem;
  font-size: $font-size-sm;

  &:focus-visible {
    outline: none;
    box-shadow: none;
  }

  &::placeholder {
    font-size: $font-size-sm;
    color: $neutral-300;
  }
}

.autocomplete-list {
  list-style: none;
  background-color: white;
  top: 40px;
  z-index: 11000000;
  border: 1px solid $bluish-gray-200;
  overflow: auto;
  max-height: 300px;
  width: fit-content;

  .autocomplete-item {
    padding: 10px;
    cursor: pointer;
    color: $neutral-400;

    &:hover {
      background-color: $bluish-gray-100;
    }
  }
}

.invalid-feedback {
  display: block !important;
}

.focused {
  .inp-wrapper {
    border: 1px solid var(--primary-200) !important;
  }
}

:deep {
  .form-control:disabled {
    background: $neutral-50 !important;
  }
  input {
    border: none !important;
    color: $neutral-400;
  }
}

.inp-wrapper {
  border: 1px solid $neutral-150;
  padding-right: 0.5rem !important;
  border-radius: 8px;

  &.disabled {
    background: $neutral-50 !important;
  }
}

.label {
  font-size: $font-size-sm;
  color: $neutral-350;
  margin-bottom: 0.5rem;
}

.invalid-feedback {
  margin-bottom: 4px;
  margin-top: 0.625rem;
  color: $red-150;
  font-size: $font-size-xs;
}
</style>
